import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n } from '../i18n/I18nContext';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import './WalletPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Wallet {
  balance: number;
  lifetime_earned: number;
  lifetime_spent: number;
}

interface Transaction {
  id: number;
  type: 'earn' | 'spend';
  amount: number;
  reason: string;
  balance_after: number;
  created_at: string;
}

const REASON_KEY: Record<string, string> = {
  solo_win: 'wallet.reasonSoloWin',
  daily_solve: 'wallet.reasonDailySolve',
  level_up: 'wallet.reasonLevelUp',
  achievement: 'wallet.reasonAchievement',
  rewarded_video: 'wallet.reasonRewarded',
  bonus_hint: 'wallet.reasonBonusHint',
  iap_pack: 'wallet.reasonIapPack',
  admin_grant: 'wallet.reasonAdmin',
  season_top: 'wallet.reasonSeasonTop',
};

export function WalletPage() {
  const { user, token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [wRes, txRes] = await Promise.all([
        fetch(`${API}/wallet/me`, { headers }),
        fetch(`${API}/wallet/transactions?limit=50`, { headers }),
      ]);
      if (wRes.ok) setWallet(await wRes.json());
      if (txRes.ok) setTransactions(await txRes.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  const formatReason = (reason: string) => {
    const key = REASON_KEY[reason];
    if (!key) return reason;
    const parts = key.split('.');
    let val: any = t;
    for (const p of parts) val = val?.[p];
    return val ?? reason;
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  if (!user) {
    return (
      <div className="wallet-page">
        <AppHeader onBack={smartBack} backLabel={t.common.back} />
        <div className="wallet-empty">
          <p>{t.premium.loginRequired}</p>
          <button className="primary-btn" onClick={() => navigate('/auth')}>
            {t.premium.goLogin}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <AppHeader onBack={smartBack} backLabel={t.common.back} />
      <div className="wallet-container">
        {/* Hero balance */}
        <div className="wallet-hero">
          <div className="wallet-coin-icon">🪙</div>
          <div className="wallet-balance-label">{t.wallet.balance}</div>
          <div className="wallet-balance-value">
            {loading ? '…' : (wallet?.balance ?? 0).toLocaleString()}
          </div>
          <div className="wallet-stats-row">
            <div>
              <span className="wallet-stat-label">{t.wallet.earned}</span>
              <span className="wallet-stat-value">{(wallet?.lifetime_earned ?? 0).toLocaleString()}</span>
            </div>
            <div>
              <span className="wallet-stat-label">{t.wallet.spent}</span>
              <span className="wallet-stat-value">{(wallet?.lifetime_spent ?? 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="wallet-section">
          <h2>{t.wallet.transactionsTitle}</h2>
          {transactions.length === 0 ? (
            <p className="wallet-empty-tx">{t.wallet.noTransactions}</p>
          ) : (
            <ul className="wallet-tx-list">
              {transactions.map(tx => (
                <li key={tx.id} className={`wallet-tx-item ${tx.type}`}>
                  <div className="tx-icon">{tx.type === 'earn' ? '+' : '−'}</div>
                  <div className="tx-content">
                    <div className="tx-reason">{formatReason(tx.reason)}</div>
                    <div className="tx-date">{formatDate(tx.created_at)}</div>
                  </div>
                  <div className={`tx-amount ${tx.type}`}>
                    {tx.type === 'earn' ? '+' : '−'}{tx.amount.toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
